import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Heart,
  Share2,
  ShoppingBag,
  Package,
} from 'lucide-react';
import { productsApi } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const toImageUrl = (value) => {
    if (!value || typeof value !== 'string') return '/placeholder.png';
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    if (value.startsWith('data:image')) return value;
    return `${BACKEND_URL}${value.startsWith('/') ? value : `/${value}`}`;
  };

  const normalizeProduct = (data) => {
    const rawProduct = data?.product || data?.data || data;

    if (!rawProduct) return null;

    const gallery = Array.isArray(rawProduct.images)
      ? rawProduct.images
          .map((item) => {
            if (typeof item === 'string') return { img: item };
            if (item && typeof item === 'object' && item.img) return { img: item.img };
            return null;
          })
          .filter(Boolean)
      : [];

    return {
      _id: rawProduct._id || rawProduct.id || '',
      productName: rawProduct.productName || '',
      productType: rawProduct.productType || '',
      productPrice: Number(rawProduct.productPrice) || 0,
      productCategory: rawProduct.productCategory || '',
      mainImg: rawProduct.mainImg || '',
      images: gallery,
      description: rawProduct.description || '',
      createdAt: rawProduct.createdAt,
      updatedAt: rawProduct.updatedAt,
    };
  };

  const fetchProduct = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await productsApi.getById(id);
      const normalized = normalizeProduct(response?.data);

      if (!normalized?._id) {
        throw new Error('Product not found');
      }

      setProduct(normalized);
    } catch (err) {
      console.error('Error fetching product:', err);
      setError('Məhsul tapılmadı.');
      setProduct(null);
      toast.error('Məhsul məlumatını yükləmək mümkün olmadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProduct();
  }, [id]);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [product?._id]);

  const images = useMemo(() => {
    if (!product) return ['/placeholder.png'];

    const list = [];
    const seen = new Set();

    if (product.mainImg) {
      const main = toImageUrl(product.mainImg);
      if (!seen.has(main)) {
        list.push(main);
        seen.add(main);
      }
    }

    if (Array.isArray(product.images)) {
      product.images.forEach((item) => {
        if (!item?.img) return;
        const imgUrl = toImageUrl(item.img);
        if (!seen.has(imgUrl)) {
          list.push(imgUrl);
          seen.add(imgUrl);
        }
      });
    }

    if (list.length === 0) {
      list.push('/placeholder.png');
    }

    return list;
  }, [product]);

  const nextImage = () => {
    if (images.length <= 1) return;
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    if (images.length <= 1) return;
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Məhsul yüklənir..." />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center"
        data-testid="product-detail-error"
      >
        <p className="text-red-500 mb-4">{error || 'Məhsul tapılmadı'}</p>
        <Link
          to="/products"
          className="inline-flex items-center gap-2 text-brand-primary font-medium"
          data-testid="back-to-products"
        >
          <ArrowLeft className="w-4 h-4" />
          Məhsullara qayıt
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="product-detail-page">
      <div className="py-4 border-b border-brand-secondary/30 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-brand-muted hover:text-brand-primary transition-colors"
            data-testid="breadcrumb-back"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Məhsullara qayıt</span>
          </Link>
        </div>
      </div>

      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div className="space-y-4">
              <div className="relative aspect-[3/4] bg-white rounded-3xl overflow-hidden shadow-warm">
                {images[currentImageIndex] !== '/placeholder.png' ? (
                  <img
                    src={images[currentImageIndex]}
                    alt={product.productName || 'Məhsul'}
                    className="w-full h-full object-cover"
                    data-testid="product-main-image"
                    onError={(e) => {
                      e.currentTarget.src = '/placeholder.png';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-16 h-16 text-brand-muted" />
                  </div>
                )}

                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
                      data-testid="prev-image-btn"
                    >
                      <ChevronLeft className="w-6 h-6 text-brand-brown" />
                    </button>

                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
                      data-testid="next-image-btn"
                    >
                      <ChevronRight className="w-6 h-6 text-brand-brown" />
                    </button>

                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/90 px-4 py-2 rounded-full shadow-lg">
                      <span className="text-sm font-medium text-brand-brown">
                        {currentImageIndex + 1} / {images.length}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {images.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {images.map((img, index) => (
                    <button
                      key={`${img}-${index}`}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`flex-shrink-0 w-20 h-24 rounded-xl overflow-hidden border-2 transition-all ${
                        currentImageIndex === index
                          ? 'border-brand-primary'
                          : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                      data-testid={`thumbnail-${index}`}
                    >
                      <img
                        src={img}
                        alt={`${product.productName} ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = '/placeholder.png';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="lg:py-8">
              {product.productCategory && (
                <span
                  className="inline-flex items-center gap-2 bg-brand-accent/30 text-brand-primary text-sm font-medium px-4 py-2 rounded-full"
                  data-testid="product-category"
                >
                  <ShoppingBag className="w-4 h-4" />
                  {product.productCategory}
                </span>
              )}

              <h1
                className="font-serif text-4xl md:text-5xl text-brand-brown mt-4"
                data-testid="product-name"
              >
                {product.productName}
              </h1>

              {product.productType && (
                <p className="text-brand-muted text-lg mt-2" data-testid="product-type">
                  {product.productType}
                </p>
              )}

              <p
                className="font-serif text-4xl text-brand-primary mt-6"
                data-testid="product-price"
              >
                {Number(product.productPrice || 0).toFixed(2)} ₼
              </p>

              <div className="flex gap-4 mt-8">
                <Link
                  to="/contact"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-primary to-[#E08E73] text-white px-8 py-4 font-medium hover:shadow-warm-lg hover:scale-105 transition-all duration-300"
                  data-testid="contact-for-purchase"
                >
                  Sifariş et
                </Link>

                <button className="w-14 h-14 rounded-full border-2 border-brand-secondary flex items-center justify-center text-brand-muted hover:text-brand-primary hover:border-brand-primary transition-colors">
                  <Heart className="w-6 h-6" />
                </button>

                <button className="w-14 h-14 rounded-full border-2 border-brand-secondary flex items-center justify-center text-brand-muted hover:text-brand-primary hover:border-brand-primary transition-colors">
                  <Share2 className="w-6 h-6" />
                </button>
              </div>

              <div className="mt-10 pt-8 border-t border-brand-secondary/50">
                <h3 className="font-serif text-xl text-brand-brown mb-4">
                  Məhsul haqqında
                </h3>
                <p className="text-brand-muted leading-relaxed">
                  {product.description ||
                    'Bu məhsul premium keyfiyyətli materiallardan hazırlanmışdır. Gündəlik istifadə və xüsusi kombinlər üçün uyğundur.'}
                </p>
              </div>

              <div className="mt-8 pt-8 border-t border-brand-secondary/50">
                <h3 className="font-serif text-xl text-brand-brown mb-4">
                  Ətraflı məlumat
                </h3>

                <ul className="space-y-3">
                  <li className="flex justify-between py-3 border-b border-brand-secondary/30">
                    <span className="text-brand-muted">Ad</span>
                    <span className="text-brand-brown font-medium">
                      {product.productName}
                    </span>
                  </li>

                  {product.productType && (
                    <li className="flex justify-between py-3 border-b border-brand-secondary/30">
                      <span className="text-brand-muted">Model</span>
                      <span className="text-brand-brown font-medium">
                        {product.productType}
                      </span>
                    </li>
                  )}

                  {product.productCategory && (
                    <li className="flex justify-between py-3 border-b border-brand-secondary/30">
                      <span className="text-brand-muted">Kateqoriya</span>
                      <span className="text-brand-brown font-medium">
                        {product.productCategory}
                      </span>
                    </li>
                  )}

                  <li className="flex justify-between py-3 border-b border-brand-secondary/30">
                    <span className="text-brand-muted">Qiymət</span>
                    <span className="text-brand-brown font-medium">
                      {Number(product.productPrice || 0).toFixed(2)} ₼
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetailPage;